// API Client for portal backend

import type {
  LoginResponse, VerifyResponse, DashboardData, RotateResponse,
  CheckoutConfig, PaddleConfig, AirgapKey, AirgapKeyResult, GenerateAirgapKeyRequest,
  Plan, CapabilityCatalog, IssuedLicense, IssueLicenseRequest, CapabilityOverrides,
  CatalogListResponse, CatalogPreviewResponse
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://license.intrigsoft.com';

export class ApiClient {
  private getHeaders(includeAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('sessionToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async requestLogin(email: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE}/portal/auth/request-login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Failed to request login');
    }

    return response.json();
  }

  async verifyToken(token: string): Promise<VerifyResponse> {
    const response = await fetch(`${API_BASE}/portal/auth/verify?token=${token}`);

    if (!response.ok) {
      throw new Error('Invalid or expired token');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    await fetch(`${API_BASE}/portal/auth/logout`, {
      method: 'POST',
      headers: this.getHeaders(true),
    });
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userEmail');
  }

  async getDashboard(): Promise<DashboardData> {
    const response = await fetch(`${API_BASE}/portal/dashboard`, {
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('sessionToken');
        throw new Error('Session expired');
      }
      throw new Error('Failed to fetch dashboard');
    }

    return response.json();
  }

  async rotateLicense(licenseId: string): Promise<RotateResponse> {
    const response = await fetch(`${API_BASE}/portal/license/rotate`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ licenseId }),
    });

    if (!response.ok) {
      throw new Error('Failed to rotate license');
    }

    return response.json();
  }

  async updateSubscriptionLabel(
    subscriptionId: string,
    name?: string | null,
    description?: string | null
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/portal/subscription/${subscriptionId}`, {
      method: 'PATCH',
      headers: this.getHeaders(true),
      body: JSON.stringify({ name, description }),
    });

    if (!response.ok) {
      throw new Error('Failed to update subscription label');
    }

    return response.json();
  }

  async getPaddleConfig(): Promise<PaddleConfig> {
    const response = await fetch(`${API_BASE}/portal/paddle-config`);

    if (!response.ok) {
      throw new Error('Failed to get Paddle config');
    }

    return response.json();
  }

  async getCheckoutConfig(subscriptionId: string): Promise<CheckoutConfig> {
    const response = await fetch(`${API_BASE}/portal/subscription/${subscriptionId}/checkout-config`, {
      method: 'POST',
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      throw new Error('Failed to get checkout config');
    }

    return response.json();
  }
  /** Fetch (or create, once per email) the signed-in user's Free key. */
  async getFreeKey(): Promise<{ licenseKey: string; created: boolean }> {
    return this.send('POST', '/portal/free-key', undefined, 'Failed to get a free key');
  }

  async listAirgapKeys(): Promise<{ keys: AirgapKey[] }> {
    return this.send('GET', '/portal/airgap/keys', undefined, 'Failed to list air-gap keys');
  }

  async generateAirgapKey(request: GenerateAirgapKeyRequest): Promise<AirgapKeyResult> {
    return this.send('POST', '/portal/airgap/generate', request, 'Failed to generate air-gap key');
  }

  async renewAirgapKey(id: string, durationDays: number): Promise<AirgapKeyResult> {
    return this.send('POST', `/portal/airgap/${id}/renew`, { durationDays }, 'Failed to renew air-gap key');
  }

  async getCapabilities(): Promise<CapabilityCatalog> {
    return this.send('GET', '/portal/capabilities', undefined, 'Failed to load capabilities');
  }

  async listPlans(): Promise<{ plans: Plan[] }> {
    return this.send('GET', '/portal/plans', undefined, 'Failed to list plans');
  }

  async savePlan(plan: Plan): Promise<Plan> {
    return this.send('PUT', `/portal/plans/${plan.id}`, plan, 'Failed to save plan');
  }

  async listIssuedLicenses(): Promise<{ licenses: IssuedLicense[] }> {
    return this.send('GET', '/portal/licenses', undefined, 'Failed to list licenses');
  }

  async issueLicense(request: IssueLicenseRequest): Promise<{ id: string; licenseKey: string; expiresAt: string | null }> {
    return this.send('POST', '/portal/licenses/issue', request, 'Failed to issue license');
  }

  async updateLicenseEntitlement(
    id: string,
    update: { planId?: string; overrides?: CapabilityOverrides | null },
  ): Promise<{ success: boolean }> {
    return this.send('PATCH', `/portal/licenses/${encodeURIComponent(id)}/entitlement`, update, 'Failed to update entitlement');
  }

  /** Authenticated JSON request; surfaces the server's message when it sends one. */
  private async send<T>(method: string, path: string, body: unknown, failure: string): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: this.getHeaders(true),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null);
      throw new Error(detail?.message ? `${failure}: ${detail.message}` : failure);
    }
    return response.json();
  }

  async requestEarlyAccess(
    email: string,
    tier: 'free' | 'pro',
    source?: string
  ): Promise<{ ok: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/early-access/signup`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, tier, source }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error((data as { message?: string }).message || 'Failed to register for early access');
    }

    return data as { ok: boolean; message: string };
  }

  // ── Model catalog (master email only) ──

  async listCatalog(): Promise<CatalogListResponse> {
    const response = await fetch(`${API_BASE}/portal/catalog/models`, {
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      throw new Error('Failed to load the model catalog');
    }

    return response.json();
  }

  /** Dry run — reports what an upload would change, writes nothing. */
  async previewCatalog(models: unknown[]): Promise<CatalogPreviewResponse> {
    return this.postCatalog('preview', models);
  }

  /** Replace the live catalog: upsert what is supplied, archive what is absent. */
  async applyCatalog(models: unknown[]): Promise<CatalogPreviewResponse> {
    return this.postCatalog('apply', models);
  }

  private async postCatalog(
    action: 'preview' | 'apply',
    models: unknown[],
  ): Promise<CatalogPreviewResponse> {
    const response = await fetch(`${API_BASE}/portal/catalog/${action}`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ models }),
    });

    const data = (await response.json().catch(() => ({}))) as CatalogPreviewResponse;

    // A 400 here is a validation result the operator needs to read in full, not
    // a failure to report generically — so return the body rather than throwing.
    if (!response.ok && !Array.isArray(data?.problems)) {
      throw new Error(`Failed to ${action} the catalog`);
    }

    return data;
  }

  async withdrawCatalogModel(id: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/portal/catalog/${encodeURIComponent(id)}/withdraw`,
      { method: 'POST', headers: this.getHeaders(true) },
    );

    if (!response.ok) {
      throw new Error('Failed to withdraw the model');
    }
  }
}

export const api = new ApiClient();
